import { formatAedCompact, formatAedNear, formatNumber, formatNumberNear, type Marks } from "@ncp/i18n";
import { bi, type Bi, type Tone } from "./engine";

// ---------------------------------------------------------------- the back office's own words

type Fixed = (key: string, options?: Record<string, unknown>) => string;
let catalogue: { en: Fixed; ar: Fixed } | null = null;

/**
 * Binds the back office's catalogue once (the page does it before a mission starts), so the agents' text uses the platform's own words: the
 * file's six stage words, the band names, and counted words in the catalogue's plural forms – Arabic counts agree with their number
 * (`_zero`, `_one`, `_two`, `_few`, `_many`, `_other`), as on every other screen.
 */
export function bindCatalogue(i18n: { getFixedT: (lng: string, ns: string) => unknown }): void {
  catalogue = { en: i18n.getFixedT("en", "backoffice") as Fixed, ar: i18n.getFixedT("ar", "backoffice") as Fixed };
}

/** A catalogue entry of the back office in both languages (the key without its `backoffice:` namespace). */
export const said = (key: string, options?: Record<string, unknown>): Bi =>
  catalogue ? bi(catalogue.en(key, options), catalogue.ar(key, options)) : bi(options?.count != null ? `${String(options.count)} ${key.split(".").pop()}` : key.split(".").pop() ?? key);

/** The counted words the agents use; each has its forms in agentic.count of both catalogues. */
export type CountNoun =
  | "days" | "workingDays" | "documents" | "parties" | "markets" | "agents" | "toolCalls" | "sources" | "decisions" | "practices" | "memories"
  | "gaps" | "letters" | "redactions" | "sections" | "figures" | "firms" | "years" | "passages" | "instruments" | "items" | "pages" | "entities"
  | "links" | "nodes" | "queries" | "discrepancies" | "views" | "objections" | "authorities" | "regulators" | "facts" | "characters" | "checklistItems"
  | "acquirers" | "points" | "licences" | "records" | "elements" | "checks";

/** "3 documents" / «3 مستندات», "11 days" / «11 يوماً»: a count with its noun in the agreement the number takes. */
export const count = (n: number, noun: CountNoun): Bi => said(`agentic.count.${noun}`, { count: n });

/** A band of the published method in words ("moderately concentrated" / «متوسطة التركز»), as the review screens write it. */
export const bandWord = (band: string | null | undefined): Bi => (band ? said(`review.sourceStatus.${band}`) : bi("—"));

/** The first letter in capitals, for an English word that opens a line. */
export const capital = (text: Bi): Bi => bi(text.en.charAt(0).toUpperCase() + text.en.slice(1), text.ar);

/** Number formats in both languages (Arabic keeps Western digits, as the rest of the platform does). */
export const num = (n: number, digits = 0): Bi => bi(formatNumber(n, "en", { maximumFractionDigits: digits }), formatNumber(n, "ar", { maximumFractionDigits: digits }));
export const pct = (n: number, digits = 1): Bi => bi(`${formatNumber(n, "en", { maximumFractionDigits: digits })}%`, `${formatNumber(n, "ar", { maximumFractionDigits: digits })}%`);
export const aed = (n: number): Bi => bi(formatAedCompact(n, "en"), formatAedCompact(n, "ar"));
export const aedFull = (n: number): Bi => bi(`AED ${formatNumber(n, "en")}`, `${formatNumber(n, "ar")} درهم`);

/**
 * Condition 2.9: a figure printed beside a mark – a threshold, a band, a control stake – at its usual precision, or in full where rounding would
 * put it on or across the mark (40.04% is never "40%", an HHI of 1,999.6 never "2,000", AED 300,000,001 never "AED 300 m").
 */
export const numNear = (n: number, marks: Marks, digits = 0): Bi => bi(formatNumberNear(n, "en", marks, digits), formatNumberNear(n, "ar", marks, digits));
export const pctNear = (n: number, marks: Marks, digits = 1): Bi => bi(`${formatNumberNear(n, "en", marks, digits)}%`, `${formatNumberNear(n, "ar", marks, digits)}%`);
export const aedNear = (n: number, marks: Marks): Bi => bi(formatAedNear(n, "en", marks), formatAedNear(n, "ar", marks));

const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const day = (iso: string | null | undefined): Bi => {
  if (!iso) return bi("—");
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return bi(iso);
  return bi(`${d.getUTCDate()} ${MONTHS_EN[d.getUTCMonth()]} ${d.getUTCFullYear()}`, `${d.getUTCDate()} ${MONTHS_AR[d.getUTCMonth()]} ${d.getUTCFullYear()}`);
};

/** A party's or market's name in Arabic when the platform has it. */
export const named = (en: string, ar?: string | null): Bi => bi(en, ar || en);

/** Document categories as the officer knows them. */
export const DOC_TYPES: Record<string, Bi> = {
  TransactionAgreement: bi("Transaction agreement", "اتفاقية الصفقة"),
  FinancialStatements: bi("Audited financial statements", "قوائم مالية مدققة"),
  MarketReport: bi("Market report", "تقرير السوق"),
  OrgChart: bi("Ownership structure", "هيكل الملكية"),
  PowerOfAttorney: bi("Power of attorney", "وكالة"),
  OverlapAnalysis: bi("Overlap analysis", "تحليل التداخل"),
  PublicSummary: bi("Public summary", "الملخص العام"),
  ApplicationForm: bi("Application form", "نموذج الطلب"),
  BoardResolution: bi("Board resolution", "قرار مجلس الإدارة"),
  Other: bi("Other document", "مستند آخر"),
};
export const docType = (category: string | null | undefined): Bi => DOC_TYPES[category ?? "Other"] ?? bi(category ?? "Document", category ?? "مستند");

// ---------------------------------------------------------------- reading a document's extracted text

/** True for a line that is mostly readable Latin text (the extraction of Arabic presentation forms is skipped on the page view). */
function readable(line: string): boolean {
  const letters = line.replace(/[^A-Za-z؀-ۿﭐ-﻿]/g, "");
  if (letters.length < 6) return false;
  const latin = line.replace(/[^A-Za-z]/g, "").length;
  return latin / letters.length > 0.7;
}

/**
 * Splits a document's extracted text into page lines a reader can scan: sentences of the readable text, wrapped to about 78 characters.
 * Glued words from PDF extraction ("LLCEmirates") are split at the case change so the page reads naturally.
 */
export function pageLines(text: string, limit = 14): string[] {
  const clean = text
    .replace(/\[page \d+\]/g, " ")
    .replace(/([a-z])([A-Z][a-z])/g, "$1 $2")
    .replace(/([.;:])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  const sentences = clean.split(/(?<=[.;])\s+/).filter(readable);
  const lines: string[] = [];
  for (const s of sentences) {
    let rest = s.trim();
    while (rest.length > 0 && lines.length < limit) {
      if (rest.length <= 78) { lines.push(rest); break; }
      const cut = rest.lastIndexOf(" ", 78);
      lines.push(rest.slice(0, cut > 40 ? cut : 78));
      rest = rest.slice(cut > 40 ? cut + 1 : 78);
    }
    if (lines.length >= limit) break;
  }
  return lines;
}

export interface ReadFact { kind: "amount" | "percent" | "date" | "licence" | "control" | "outlets" | "party" | "year"; text: string; value?: number; tone: Tone }

const AMOUNT = /AED\s?([\d,]+(?:\.\d+)?)\s*(million|billion|bn|m)?/gi;
const PERCENT = /(\d{1,3}(?:\.\d+)?)\s?%/g;
const DATE = /\b(\d{1,2})\s(January|February|March|April|May|June|July|August|September|October|November|December)\s(20\d\d)\b/g;
const LICENCE = /\bCN-\d{5,8}\b/g;
const CONTROL = /\b(sole control|joint control|decisive influence|controlling interest)\b/gi;
const OUTLETS = /\b(\d{2,5})\s(outlets|stores|branches|plants|pharmacies)\b/gi;

/** Facts a reader can pick out of a line of text, with their character ranges for the highlight on the page. */
export function factsIn(line: string): Array<ReadFact & { from: number; to: number }> {
  const out: Array<ReadFact & { from: number; to: number }> = [];
  const scan = (re: RegExp, kind: ReadFact["kind"], value: (m: RegExpExecArray) => number | undefined, tone: Tone = "info") => {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line))) out.push({ kind, text: m[0], value: value(m), tone, from: m.index, to: m.index + m[0].length });
  };
  scan(AMOUNT, "amount", (m) => {
    const base = Number((m[1] ?? "0").replace(/,/g, ""));
    const unit = (m[2] ?? "").toLowerCase();
    return unit.startsWith("b") ? base * 1e9 : unit.startsWith("m") ? base * 1e6 : base;
  });
  scan(PERCENT, "percent", (m) => Number(m[1]));
  scan(DATE, "date", () => undefined);
  scan(LICENCE, "licence", () => undefined);
  scan(CONTROL, "control", () => undefined, "warn");
  scan(OUTLETS, "outlets", (m) => Number(m[1]));
  return out.sort((a, b) => a.from - b.from).filter((f, i, all) => i === 0 || f.from >= (all[i - 1]?.to ?? 0));
}

/** Every fact of a document's text, in reading order. */
export function factsOf(text: string): ReadFact[] {
  return pageLines(text, 60).flatMap((l) => factsIn(l).map((f): ReadFact => ({ kind: f.kind, text: f.text, value: f.value, tone: f.tone })));
}

export const MONTH_INDEX = Object.fromEntries(MONTHS_EN.map((m, i) => [m, i + 1]));
/** "30 August 2026" → "2026-08-30". */
export function isoOf(date: string): string | null {
  const m = /^(\d{1,2})\s(\w+)\s(\d{4})$/.exec(date.trim());
  if (!m) return null;
  const month = MONTH_INDEX[m[2] ?? ""];
  return month ? `${m[3]}-${String(month).padStart(2, "0")}-${String(m[1]).padStart(2, "0")}` : null;
}
